import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import CardRenderer from "@/components/cartes/CardRenderer";
import { AddToPhone } from "@/components/cartes/AddToPhone";
import { Share2, QrCode, Contact, Printer, X, RefreshCw, Download, Link2, MessageCircle, Facebook, Linkedin, Twitter } from "lucide-react";
import { qrUrl, vcardUrl, pdfUrl } from "@/lib/cartes";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api` });

export default function CartePublique() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const scanned = useRef(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return; // évite le double appel (StrictMode) qui gonfle les vues
    loaded.current = true;
    const qs = new URLSearchParams(window.location.search);
    pub.get(`/cartes/public/${slug}`).then(({ data }) => {
      setCard(data);
      setMeta("apple-mobile-web-app-title", data.identity?.display_name || data.identity?.org_name || "Ma carte");
      document.title = data.seo?.title || `${data.identity?.display_name || data.identity?.org_name || "Carte"} — PROXIreduc-28`;
      setMeta("description", data.seo?.description || data.about || "Carte de visite digitale PROXIreduc-28.");
      if (Array.isArray(data.seo?.keywords) && data.seo.keywords.length) setMeta("keywords", data.seo.keywords.join(", "));
      setMeta("robots", data.indexable ? "index,follow" : "noindex,nofollow");
      if (data.indexable) injectCardJsonLd(data, slug);
      if (qs.get("src") === "qr" && !scanned.current) {
        scanned.current = true;
        pub.post(`/cartes/public/${slug}/track`, { type: "qr_scan" }).catch(() => {});
      }
    }).catch(() => setErr(true));
  }, [slug]);

  // Branche le manifest PWA + l'icône propres à la carte, pour l'installer comme une appli.
  useEffect(() => {
    const head = document.head;
    let manifest = head.querySelector('link[rel="manifest"]');
    const prevManifest = manifest ? manifest.getAttribute("href") : null;
    if (!manifest) { manifest = document.createElement("link"); manifest.rel = "manifest"; head.appendChild(manifest); }
    manifest.setAttribute("href", `${BACKEND}/api/cartes/public/${slug}/manifest.webmanifest`);

    const apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    apple.setAttribute("sizes", "180x180");
    apple.setAttribute("href", `${BACKEND}/api/cartes/public/${slug}/icon?size=180`);
    apple.setAttribute("data-card-pwa", "1");
    head.appendChild(apple);

    const metas = [["apple-mobile-web-app-capable", "yes"], ["mobile-web-app-capable", "yes"]];
    metas.forEach(([n, c]) => {
      const m = document.createElement("meta");
      m.setAttribute("name", n); m.setAttribute("content", c); m.setAttribute("data-card-pwa", "1");
      head.appendChild(m);
    });

    return () => {
      if (prevManifest) manifest.setAttribute("href", prevManifest);
      head.querySelectorAll('[data-card-pwa="1"]').forEach((n) => n.remove());
    };
  }, [slug]);

  const track = (type, key) => { pub.post(`/cartes/public/${slug}/track`, { type, key }).catch(() => {}); };

  const shareUrl = `${BACKEND}/api/cartes/og/${slug}`;  // lien à pré-rendu OG (aperçu garanti au partage)
  const cardName = card?.identity?.display_name || card?.identity?.org_name || "ma carte";
  const shareText = `Découvrez ${cardName} sur PROXIreduc-28`;

  const openShare = (reseau, href) => { track("share", reseau); window.open(href, "_blank", "noopener,noreferrer"); };

  const shareWhatsapp = () => openShare("whatsapp", `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`);
  const shareFacebook = () => openShare("facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  const shareLinkedin = () => openShare("linkedin", `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`);
  const shareTwitter = () => openShare("twitter", `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`);

  const copyLink = async () => {
    track("share", "copy");
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Lien copié !"); }
    catch { window.prompt("Copiez le lien :", shareUrl); }
  };

  const downloadImage = () => { track("share", "image"); window.open(`${BACKEND}/api/cartes/public/${slug}/image?format=story`, "_blank"); };

  const addContact = () => { window.location.href = vcardUrl(slug); };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-center px-6"><div><div className="text-5xl mb-3">🔍</div><p className="text-slate-600 font-semibold">Cette carte n'existe pas ou n'est plus disponible.</p></div></div>;
  if (!card) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400"><RefreshCw className="animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-100 flex justify-center">
      <div className="w-full max-w-md bg-white min-h-screen relative shadow-xl" data-testid="carte-publique">
        <div className="pb-28"><CardRenderer card={card} onTrack={track} /></div>

        {/* Barre d'actions fixe */}
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md px-3 pb-3 pt-2 bg-gradient-to-t from-white via-white to-transparent" data-testid="share-bar">
          <div className="grid grid-cols-3 gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="btn-share" className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-slate-900 text-white font-semibold text-xs active:scale-95 transition-transform w-full"><Share2 size={18} /> Partager</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" side="top" sideOffset={8} className="w-56" data-testid="share-menu">
                <DropdownMenuItem onClick={shareWhatsapp} data-testid="share-whatsapp" className="cursor-pointer gap-2.5"><MessageCircle size={16} className="text-[#25D366]" /> WhatsApp</DropdownMenuItem>
                <DropdownMenuItem onClick={shareFacebook} data-testid="share-facebook" className="cursor-pointer gap-2.5"><Facebook size={16} className="text-[#1877F2]" /> Facebook</DropdownMenuItem>
                <DropdownMenuItem onClick={shareLinkedin} data-testid="share-linkedin" className="cursor-pointer gap-2.5"><Linkedin size={16} className="text-[#0A66C2]" /> LinkedIn</DropdownMenuItem>
                <DropdownMenuItem onClick={shareTwitter} data-testid="share-twitter" className="cursor-pointer gap-2.5"><Twitter size={16} className="text-slate-900" /> X (Twitter)</DropdownMenuItem>
                <DropdownMenuItem onClick={copyLink} data-testid="share-copy" className="cursor-pointer gap-2.5"><Link2 size={16} className="text-slate-500" /> Copier le lien</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={() => { setShowQr(true); }} data-testid="btn-exchange" className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-[#E05638] text-white font-semibold text-xs active:scale-95 transition-transform"><QrCode size={18} /> Échanger</button>
            <button onClick={addContact} data-testid="btn-vcard" className="flex flex-col items-center gap-1 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-xs active:scale-95 transition-transform"><Contact size={18} /> Contact</button>
          </div>
          <div className="mt-2">
            <AddToPhone slug={slug} name={cardName} />
          </div>
          <div className="mt-2 flex items-center justify-center gap-4">
            <button onClick={downloadImage} data-testid="btn-download-image" className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-[#E05638] font-semibold"><Download size={13} /> Télécharger l'image (story / statut)</button>
            <a href={pdfUrl(slug)} target="_blank" rel="noreferrer" onClick={() => track("click", "print")} data-testid="btn-print" className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600"><Printer size={13} /> Version imprimable (PDF)</a>
          </div>
        </div>

        {/* Modale échange / QR */}
        {showQr && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowQr(false)} data-testid="qr-modal">
            <div className="bg-white rounded-3xl p-6 max-w-xs w-full text-center relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowQr(false)} className="absolute top-3 right-3 text-slate-400"><X size={20} /></button>
              <h3 className="font-bold text-slate-900 mb-1">Échangeons nos cartes</h3>
              <p className="text-xs text-slate-500 mb-4">Faites scanner ce QR code avec l'appareil photo d'un autre smartphone — aucune application requise.</p>
              <img src={`${qrUrl(slug)}`} alt="QR code" className="w-56 h-56 mx-auto rounded-xl border border-slate-100" data-testid="qr-image" />
              <button onClick={downloadImage} className="mt-4 w-full py-2.5 rounded-xl bg-[#E05638] text-white font-semibold text-sm flex items-center justify-center gap-2" data-testid="qr-download-image"><Download size={16} /> Télécharger l'image à partager</button>
              <div className="mt-3 flex items-center justify-center gap-3" data-testid="qr-share-networks">
                <button onClick={shareWhatsapp} title="WhatsApp" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center active:scale-95 transition-transform" data-testid="qr-share-whatsapp"><MessageCircle size={18} className="text-[#25D366]" /></button>
                <button onClick={shareFacebook} title="Facebook" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center active:scale-95 transition-transform" data-testid="qr-share-facebook"><Facebook size={18} className="text-[#1877F2]" /></button>
                <button onClick={shareLinkedin} title="LinkedIn" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center active:scale-95 transition-transform" data-testid="qr-share-linkedin"><Linkedin size={18} className="text-[#0A66C2]" /></button>
                <button onClick={shareTwitter} title="X (Twitter)" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center active:scale-95 transition-transform" data-testid="qr-share-twitter"><Twitter size={18} className="text-slate-900" /></button>
                <button onClick={copyLink} title="Copier le lien" className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center active:scale-95 transition-transform" data-testid="qr-share-copy"><Link2 size={18} className="text-slate-500" /></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
}

// Données structurées de la carte digitale (ProfilePage + Person/Organization).
// Injectées uniquement pour les cartes publiques indexables → aide Google & moteurs IA.
function injectCardJsonLd(data, slug) {
  try {
    const id = data.identity || {};
    const name = id.display_name || id.org_name || "Carte de visite";
    const isOrg = !!id.org_name && !id.display_name;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/carte/${slug}`;
    const entity = {
      "@type": isOrg ? "Organization" : "Person",
      name,
      ...(id.job_title || id.metier ? { jobTitle: id.job_title || id.metier } : {}),
      ...(data.about ? { description: data.about } : {}),
      ...(id.city ? { address: { "@type": "PostalAddress", addressLocality: id.city, addressRegion: "Eure-et-Loir", addressCountry: "FR" } } : {}),
      ...(id.phone ? { telephone: id.phone } : {}),
      ...(id.email ? { email: id.email } : {}),
      ...(id.website ? { url: id.website } : {}),
      ...(id.avatar_url || id.logo_url ? { image: id.avatar_url || id.logo_url } : {}),
    };
    const ld = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      mainEntity: entity,
      url,
      isPartOf: { "@type": "WebSite", name: "PROXIreduc-28", url: origin },
    };
    document.querySelectorAll('script[data-card-ld]').forEach((n) => n.remove());
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-card-ld", "1");
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
  } catch { /* noop */ }
}
